import React, { useState, useEffect, useRef, useCallback } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  DecoupledEditor,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  BlockToolbar,
  Bold,
  Bookmark,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  Fullscreen,
  Heading,
  Highlight,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageEditing,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageUtils,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  PlainTableOutput,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableLayout,
  TableProperties,
  TableToolbar,
  TextPartLanguage,
  TextTransformation,
  TodoList,
  Underline,
  WordCount
} from "ckeditor5";
import { SourceEditingEnhanced } from "ckeditor5-premium-features";
import "ckeditor5/ckeditor5.css";
import "ckeditor5-premium-features/ckeditor5-premium-features.css";
import { UploadEditorImage } from "../services/Templates";
const LICENSE_KEY = process.env.REACT_APP_CKEDITOR_LICENSE_KEY;

function CustomUploadAdapter(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return {
      upload: async () => {
        const file = await loader.file;
        const formData = new FormData();
        formData.append('upload', file);

        try {
          const response = await UploadEditorImage(formData);
          if (response.data.uploaded && response.data.url) {
            return { default: response.data.url };
          }
          throw new Error('Upload failed');
        } catch (error) {
          throw new Error('Upload failed: ' + (error.response?.data?.error?.message || error.message));
        }
      },
      abort: () => { }
    };
  };
}
// Different colors for each comment
const HIGHLIGHT_COLORS = [
  { bg: "rgba(255, 235, 59, 0.3)", border: "#FBC02D", active: "rgba(255, 193, 7, 0.7)" }, // Yellow
  { bg: "rgba(129, 212, 250, 0.3)", border: "#0288D1", active: "rgba(3, 169, 244, 0.7)" }, // Blue
  { bg: "rgba(165, 214, 167, 0.3)", border: "#388E3C", active: "rgba(76, 175, 80, 0.7)" }, // Green
  { bg: "rgba(255, 183, 77, 0.3)", border: "#F57C00", active: "rgba(255, 152, 0, 0.7)" }, // Orange
  { bg: "rgba(186, 104, 200, 0.3)", border: "#7B1FA2", active: "rgba(156, 39, 176, 0.7)" }, // Purple
];

const DocxEditorCom = React.forwardRef(
  ({ htmlContent = "", comments = [], onContentChange, onHighlightClick }, ref) => {
    const [content, setContent] = useState(htmlContent);
    const editorRef = useRef(null);
    const editorContainerRef = useRef(null);
    const toolbarRef = useRef(null);
    const menuBarRef = useRef(null);
    const wordCountRef = useRef(null);
    const highlightsAppliedRef = useRef(false);

    React.useImperativeHandle(ref, () => ({
      scrollToComment: (position, commentId) => scrollToHighlight(position, commentId),
    }));

    useEffect(() => {
      setContent(htmlContent);
      highlightsAppliedRef.current = false;
    }, [htmlContent]);

    /** Scroll to highlight using position offsets (not comment ID) */
    const scrollToHighlight = (position, commentId) => {
      const editable = editorRef.current?.ui?.view?.editable?.element;
      if (!editable || !position) return false;

      const { element_id, start_offset, end_offset } = position;

      if (!element_id || start_offset === undefined || end_offset === undefined) {
        console.warn("⚠️ Invalid position data");
        return false;
      }

      // Find the target element
      const targetElement = editable.querySelector(`#${element_id}`);
      if (!targetElement) {
        console.warn(`⚠️ Element #${element_id} not found`);
        return false;
      }

      // Find the exact highlight span by matching position data
      const allHighlights = editable.querySelectorAll(".comment-highlight");
      let targetHighlight = null;

      // First try to find by comment ID (as fallback)
      if (commentId) {
        targetHighlight = editable.querySelector(`[data-comment-id="${commentId}"]`);
      }

      // If not found by ID, find by position within the element
      if (!targetHighlight) {
        const walker = document.createTreeWalker(
          targetElement,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );

        const textNodes = [];
        while (walker.nextNode()) {
          if (walker.currentNode.textContent.length > 0) {
            textNodes.push(walker.currentNode);
          }
        }

        let currentOffset = 0;
        for (const textNode of textNodes) {
          const nodeLength = textNode.textContent.length;
          const nodeEnd = currentOffset + nodeLength;

          if (start_offset >= currentOffset && start_offset < nodeEnd) {
            let parent = textNode.parentElement;
            while (parent && parent !== targetElement) {
              if (parent.classList.contains("comment-highlight")) {
                targetHighlight = parent;
                break;
              }
              parent = parent.parentElement;
            }
            break;
          }

          currentOffset += nodeLength;
        }
      }

      if (targetHighlight) {
        // Remove all active states
        allHighlights.forEach((span) => {
          span.classList.remove("active-highlight", "blink-active");
          const colorIndex = parseInt(span.dataset.colorIndex) || 0;
          const color = HIGHLIGHT_COLORS[colorIndex % HIGHLIGHT_COLORS.length];
          span.style.backgroundColor = color.bg;
          span.style.borderBottom = `2px solid ${color.border}`;
        });

        // Scroll to element
        targetHighlight.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        // Activate this highlight
        setTimeout(() => {
          const colorIndex = parseInt(targetHighlight.dataset.colorIndex) || 0;
          const color = HIGHLIGHT_COLORS[colorIndex % HIGHLIGHT_COLORS.length];

          targetHighlight.classList.add("active-highlight", "blink-active");
          targetHighlight.style.backgroundColor = color.active;
          targetHighlight.style.borderBottom = `3px solid ${color.border}`;

          setTimeout(() => {
            targetHighlight.classList.remove("blink-active");
          }, 2500);
        }, 100);

        return true;
      }

      return false;
    };

    /** Apply all comment highlights using offset positions */
    const applyHighlights = useCallback(() => {
      const editor = editorRef.current;
      if (!editor || !comments?.length) return;

      const editable = editor.ui.view.editable.element;
      if (!editable) return;

      // Remove old highlights
      const oldHighlights = editable.querySelectorAll(".comment-highlight");
      oldHighlights.forEach((span) => {
        const parent = span.parentNode;
        if (parent) {
          while (span.firstChild) {
            parent.insertBefore(span.firstChild, span);
          }
          parent.removeChild(span);
        }
      });
      editable.normalize();

      let successCount = 0;

      comments.forEach((comment, commentIndex) => {
        try {
          const { element_id, start_offset, end_offset } = comment.position || {};

          if (!element_id || start_offset === undefined || end_offset === undefined) {
            console.warn(`⚠️ Invalid position for comment ${comment.id}`);
            return;
          }

          const targetElement = editable.querySelector(`#${element_id}`);

          if (!targetElement) {
            console.warn(`⚠️ Element #${element_id} not found`);
            return;
          }

          const color = HIGHLIGHT_COLORS[commentIndex % HIGHLIGHT_COLORS.length];

          const textNodes = [];
          const walker = document.createTreeWalker(
            targetElement,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );

          while (walker.nextNode()) {
            if (walker.currentNode.textContent.length > 0) {
              textNodes.push(walker.currentNode);
            }
          }

          if (textNodes.length === 0) return;

          let currentOffset = 0;
          let startNode = null;
          let endNode = null;
          let startNodeOffset = 0;
          let endNodeOffset = 0;

          for (let i = 0; i < textNodes.length; i++) {
            const textNode = textNodes[i];
            const nodeLength = textNode.textContent.length;
            const nodeStart = currentOffset;
            const nodeEnd = currentOffset + nodeLength;

            if (startNode === null && start_offset >= nodeStart && start_offset < nodeEnd) {
              startNode = textNode;
              startNodeOffset = start_offset - nodeStart;
            }

            if (startNode !== null && end_offset > nodeStart && end_offset <= nodeEnd) {
              endNode = textNode;
              endNodeOffset = end_offset - nodeStart;
              break;
            }

            currentOffset += nodeLength;
          }

          if (startNode && !endNode && textNodes.length > 0) {
            const lastNode = textNodes[textNodes.length - 1];
            endNode = lastNode;
            endNodeOffset = lastNode.textContent.length;
          }

          if (!startNode || !endNode) {
            console.warn(`⚠️ Range not found for comment ${comment.id}`);
            return;
          }

          const range = document.createRange();

          try {
            range.setStart(startNode, startNodeOffset);
            range.setEnd(endNode, endNodeOffset);

            if (!range.collapsed && range.toString().length > 0) {
              const highlightSpan = document.createElement("span");
              highlightSpan.className = "comment-highlight";
              highlightSpan.dataset.commentId = comment.id;
              highlightSpan.dataset.colorIndex = commentIndex;
              highlightSpan.dataset.recipientName = comment.recipient_name;
              highlightSpan.dataset.commentText = comment.comment_text;

              highlightSpan.style.cssText = `
                background-color: ${color.bg};
                border-bottom: 2px solid ${color.border};
                cursor: pointer;
                transition: all 0.2s ease;
                position: relative;
                padding: 2px 0;
                display: inline;
              `;

              highlightSpan.title = `💬 ${comment.recipient_name}: ${comment.comment_text}`;

              highlightSpan.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onHighlightClick === "function") {
                  onHighlightClick(comment.id);
                }
              });

              highlightSpan.addEventListener("mouseenter", () => {
                if (!highlightSpan.classList.contains("active-highlight")) {
                  highlightSpan.style.backgroundColor = color.active;
                  highlightSpan.style.borderBottom = `3px solid ${color.border}`;
                }
              });

              highlightSpan.addEventListener("mouseleave", () => {
                if (!highlightSpan.classList.contains("active-highlight")) {
                  highlightSpan.style.backgroundColor = color.bg;
                  highlightSpan.style.borderBottom = `2px solid ${color.border}`;
                }
              });

              range.surroundContents(highlightSpan);
              successCount++;
            }
          } catch (err) {
            // ignore
          }
        } catch (err) {
          // ignore
        }
      });

      console.log(`✅ Successfully highlighted ${successCount}/${comments.length} comments`);
      highlightsAppliedRef.current = true;
    }, [comments, onHighlightClick]);

    useEffect(() => {
      if (editorRef.current && comments?.length > 0 && !highlightsAppliedRef.current) {
        const timer = setTimeout(() => {
          applyHighlights();
        }, 800);
        return () => clearTimeout(timer);
      }
    }, [content, comments, applyHighlights]);

    const editorConfig = {
      toolbar: {
        items: [
          "undo",
          "redo",
          "|",
          "sourceEditingEnhanced",
          "|",
          "heading",
          "|",
          "fontSize",
          "fontFamily",
          "fontColor",
          "fontBackgroundColor",
          "|",
          "bold",
          "italic",
          "underline",
          "|",
          "link",
          "insertTable",
          "highlight",
          "blockQuote",
          "|",
          "alignment",
          "|",
          "bulletedList",
          "numberedList",
          "todoList",
          "outdent",
          "indent",
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        Alignment,
        Autoformat,
        AutoImage,
        AutoLink,
        Autosave,
        BalloonToolbar,
        BlockQuote,
        BlockToolbar,
        Bold,
        Bookmark,
        Essentials,
        FindAndReplace,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        FullPage,
        Fullscreen,
        Heading,
        Highlight,
        HorizontalLine,
        ImageBlock,
        ImageCaption,
        ImageEditing,
        ImageInline,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageToolbar,
        ImageUpload,
        ImageUtils,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        List,
        ListProperties,
        MediaEmbed,
        PageBreak,
        Paragraph,
        PasteFromOffice,
        PlainTableOutput,
        RemoveFormat,
        SourceEditingEnhanced,
        SpecialCharacters,
        SpecialCharactersArrows,
        SpecialCharactersCurrency,
        SpecialCharactersEssentials,
        SpecialCharactersLatin,
        SpecialCharactersMathematical,
        SpecialCharactersText,
        Strikethrough,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableLayout,
        TableProperties,
        TableToolbar,
        TextPartLanguage,
        TextTransformation,
        TodoList,
        Underline,
        WordCount,
        CustomUploadAdapter
      ],
      balloonToolbar: ["bold", "italic", "|", "link"],
      fontFamily: { supportAllValues: true },
      fontSize: { options: [10, 12, 14, "default", 18, 20, 22], supportAllValues: true },
      licenseKey: LICENSE_KEY,
    };

    return (
      <div className="main-container">
        <div
          className="editor-container editor-container_document-editor editor-container_include-style"
          ref={editorContainerRef}
        >
          <div className="editor-container__menu-bar" ref={menuBarRef}></div>
          <div className="editor-container__toolbar" ref={toolbarRef}></div>
          <div className="editor-container__editor-wrapper">
            {/* Same wrapper classes as NegotiationManage */}
            <div className="manage-ckeditor-wrapper">
              <div className="manage-ckeditor-content">
                <CKEditor
                  editor={DecoupledEditor}
                  config={editorConfig}
                  data={content}
                  onReady={(editor) => {
                    const editingView = editor.editing.view;
                    const root = editingView.document.getRoot();

                    // 🔥 SAME FULL STYLE AS NegotiationManage
                    editingView.change((writer) => {
                      writer.setStyle("width", "210mm", root);
                      writer.setStyle("min-height", "297mm", root);
                      writer.setStyle("padding", "21mm", root);
                      writer.setStyle("box-sizing", "border-box", root);
                      writer.setStyle("background", "#fff", root);
                      writer.setStyle("font-family", "Arial, sans-serif", root);
                      writer.setStyle("line-height", "1.6", root);
                      writer.setStyle("color", "#000", root);
                    });

                    // Extra content styling same as backend / NegotiationManage
                    const style = document.createElement("style");
                    style.innerHTML = `
                      .ck-content table {
                        width: 100%;
                        border-collapse: collapse;
                        page-break-inside: auto;
                        font-family: Arial, sans-serif;
                      }
                      .ck-content th, .ck-content td {
                        border: 1px solid black;
                        padding: 8px;
                        text-align: left;
                      }
                      .ck-content blockquote {
                        border-left: 4px solid #ccc;
                        margin: 10px 0;
                        padding: 10px 20px;
                        font-style: italic;
                      }
                      .ck-content p, .ck-content div {
                        margin: 10px 0;
                      }
                      .ck-content strong {
                        font-weight: bold;
                      }

                      /* highlight marker colors (same naming convention) */
                      .ck-content .marker-yellow { background-color: #ffd365 !important; }
                      .ck-content .marker-green { background-color: #c8e6c9 !important; }

                      /* Make page look like a sheet (same feel as preview) */
                      .manage-ckeditor-content .ck-editor__editable {
                        margin: 10px auto;
                        border-radius: 6px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.12);
                      }
                    `;
                    editor.ui.getEditableElement().appendChild(style);

                    const wordCount = editor.plugins.get("WordCount");
                    if (wordCountRef.current && wordCount?.wordCountContainer) {
                      wordCountRef.current.appendChild(wordCount.wordCountContainer);
                    }

                    if (toolbarRef.current) {
                      toolbarRef.current.appendChild(editor.ui.view.toolbar.element);
                    }
                    if (menuBarRef.current && editor.ui.view.menuBarView) {
                      menuBarRef.current.appendChild(editor.ui.view.menuBarView.element);
                    }

                    const pageBreakButton = document.querySelector(
                      '.ck-button[title*="Page break"]'
                    );
                    if (pageBreakButton) {
                      console.log("PageBreak button found and active");
                    }

                    editorRef.current = editor;

                    try {
                      editor.conversion.for("downcast").attributeToElement({
                        model: "manageHighlight",
                        view: (value, { writer: viewWriter }) => {
                          if (value === null || value === undefined) return null;
                          const className = `manage-highlight-${value}`;
                          return viewWriter.createAttributeElement(
                            "span",
                            { class: className },
                            { priority: 5 }
                          );
                        },
                      });

                      editor.conversion.for("upcast").elementToAttribute({
                        view: {
                          name: "span",
                          key: "class",
                          value: (viewElement) => {
                            const cls = viewElement.getAttribute("class") || "";
                            const m = cls.match(/manage-highlight-([a-z0-9\-]+)/);
                            return m ? { key: "manageHighlight", value: m[1] } : false;
                          },
                        },
                      });
                    } catch (convError) {
                      // ignore
                    }

                    setTimeout(() => {
                      applyHighlights();
                      highlightsAppliedRef.current = true;
                    }, 500);
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setContent(data);
                    if (onContentChange) onContentChange(data);
                    highlightsAppliedRef.current = false;
                  }}
                  onAfterDestroy={() => {
                    [menuBarRef, toolbarRef, wordCountRef].forEach((r) => {
                      if (r.current) r.current.innerHTML = "";
                    });
                    editorRef.current = null;
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="editor_container__word-count" ref={wordCountRef}></div>

        <style>{`
          .comment-highlight {
            position: relative;
            display: inline;
          }

          .comment-highlight::after {
            content: '💬';
            position: absolute;
            right: -18px;
            top: -8px;
            font-size: 11px;
            opacity: 0;
            transition: opacity 0.2s;
          }

          .comment-highlight:hover::after {
            opacity: 1;
          }

          .comment-highlight.active-highlight {
            box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
            z-index: 10;
          }

          .blink-active {
            animation: blink-highlight 0.5s ease-in-out 4;
          }

          @keyframes blink-highlight {
            0%, 100% { 
              transform: scale(1);
              opacity: 1;
            }
            50% { 
              transform: scale(1.02);
              opacity: 0.8;
            }
          }
        `}</style>
      </div>
    );
  }
);

DocxEditorCom.displayName = "DocxEditorCom";

export default DocxEditorCom;
